import { useState, useEffect } from 'react';
import { Sparkles, Check, Loader2, X, ChevronRight, ChevronLeft, Target, Calendar, Apple, AlertCircle, Activity, Brain, Dumbbell, Heart, Salad, Moon, Zap, Scale, Lock, Crown } from 'lucide-react';
import LongevityBot from './LongevityBot';
import { supabase } from '../lib/supabase';
import { useSubscription } from '../contexts/SubscriptionContext';

interface GenerateProtocolModalProps {
  onComplete: (protocol: any) => void;
  onClose: () => void;
  onUpgrade?: () => void; // Optional callback to navigate to subscription page
}

interface Preferences {
  currentWeightKg: number | null;
  targetWeightKg: number | null;
  targetTimelineWeeks: number;
  foodPreferences: string[];
  allergies: string[];
  dietaryRestrictions: string;
  activityLevel: string;
}

interface GenerationStep {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  status: 'pending' | 'loading' | 'completed';
}

export default function GenerateProtocolModal({ onComplete, onClose, onUpgrade }: GenerateProtocolModalProps) {
  // Subscription state
  const {
    checkProtocolGeneration,
    recordProtocolGeneration,
    isPro,
    isExpired,
  } = useSubscription();

  const [protocolCheckResult, setProtocolCheckResult] = useState<{
    allowed: boolean;
    message: string;
    daysUntilNext?: number;
  } | null>(null);
  const [isCheckingProtocol, setIsCheckingProtocol] = useState(true);

  // Existing state
  const [wizardStep, setWizardStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [currentWeight, setCurrentWeight] = useState<number | null>(null);
  const [hasStoredWeight, setHasStoredWeight] = useState<boolean | null>(null);
  const [generationSteps, setGenerationSteps] = useState<GenerationStep[]>([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  const [preferences, setPreferences] = useState<Preferences>({
    currentWeightKg: null,
    targetWeightKg: null,
    targetTimelineWeeks: 4,
    foodPreferences: [],
    allergies: [],
    dietaryRestrictions: '',
    activityLevel: 'moderately_active'
  });

  const initialGenerationSteps: GenerationStep[] = [
    {
      id: 'profile',
      label: 'Analyzing Health Profile',
      description: 'Reviewing your current health metrics and goals...',
      icon: Heart,
      status: 'pending'
    },
    {
      id: 'nutrition',
      label: 'Creating Nutrition Plan',
      description: 'Calculating optimal macros and meal suggestions...',
      icon: Salad,
      status: 'pending'
    },
    {
      id: 'exercise',
      label: 'Designing Exercise Program',
      description: 'Building progressive workout routines...',
      icon: Dumbbell,
      status: 'pending'
    },
    {
      id: 'mental',
      label: 'Planning Mental Wellness',
      description: 'Selecting mindfulness and stress management activities...',
      icon: Brain,
      status: 'pending'
    },
    {
      id: 'sleep',
      label: 'Optimizing Sleep Schedule',
      description: 'Creating recovery and sleep improvement strategies...',
      icon: Moon,
      status: 'pending'
    },
    {
      id: 'finalize',
      label: 'Finalizing Protocol',
      description: 'Assembling your personalized daily schedule...',
      icon: Zap,
      status: 'pending'
    }
  ];

  // Check if user can generate protocol on mount
  useEffect(() => {
    const checkProtocol = async () => {
      setIsCheckingProtocol(true);
      try {
        const result = await checkProtocolGeneration();
        setProtocolCheckResult(result);
      } catch (err) {
        console.error('Error checking protocol generation:', err);
        // Allow generation if check fails (fallback)
        setProtocolCheckResult({ allowed: true, message: '' });
      }
      setIsCheckingProtocol(false);
    };
    checkProtocol();
  }, [checkProtocolGeneration]);

  // Load user profile on mount to check if weight exists
  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    setIsLoadingProfile(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('weight_kg')
          .eq('user_id', session.user.id)
          .single();
        
        if (profile && profile.weight_kg && profile.weight_kg > 0) {
          setCurrentWeight(profile.weight_kg);
          setHasStoredWeight(true);
          setPreferences(prev => ({ 
            ...prev, 
            currentWeightKg: profile.weight_kg,
            targetWeightKg: profile.weight_kg 
          }));
        } else {
          setHasStoredWeight(false);
        }
      } else {
        setHasStoredWeight(false);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setHasStoredWeight(false);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const saveCurrentWeight = async (weight: number) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await supabase
          .from('user_profiles')
          .update({ weight_kg: weight })
          .eq('user_id', session.user.id);
      }
    } catch (error) {
      console.error('Error saving weight:', error);
    }
  };

  const commonFoods = [
    'Chicken', 'Fish', 'Eggs', 'Tofu', 'Rice', 'Quinoa', 'Oats',
    'Broccoli', 'Spinach', 'Sweet Potato', 'Avocado', 'Berries',
    'Nuts', 'Greek Yogurt', 'Lentils', 'Salmon'
  ];

  const commonAllergies = [
    'Peanuts', 'Tree Nuts', 'Shellfish', 'Fish', 'Eggs', 'Dairy',
    'Soy', 'Wheat', 'Gluten', 'Sesame'
  ];

  const timelineOptions = [
    { weeks: 2, label: '2 Weeks', desc: 'Quick Start', color: 'orange' },
    { weeks: 4, label: '4 Weeks', desc: 'Recommended', color: 'green' },
    { weeks: 8, label: '8 Weeks', desc: 'Sustainable', color: 'blue' },
    { weeks: 12, label: '12 Weeks', desc: 'Transformation', color: 'purple' }
  ];

  const toggleArrayItem = (array: string[], item: string) => {
    return array.includes(item)
      ? array.filter(i => i !== item)
      : [...array, item];
  };

  // Dynamic step calculation based on whether we need to ask for current weight
  const getStepConfig = () => {
    if (hasStoredWeight) {
      // Steps: 0=intro, 1=target weight, 2=timeline, 3=food, 4=allergies, 5=activity
      return {
        totalSteps: 5,
        targetWeightStep: 1,
        timelineStep: 2,
        foodStep: 3,
        allergiesStep: 4,
        activityStep: 5
      };
    } else {
      // Steps: 0=intro, 1=current weight, 2=target weight, 3=timeline, 4=food, 5=allergies, 6=activity
      return {
        totalSteps: 6,
        currentWeightStep: 1,
        targetWeightStep: 2,
        timelineStep: 3,
        foodStep: 4,
        allergiesStep: 5,
        activityStep: 6
      };
    }
  };

  const canProceedFromStep = (step: number) => {
    const config = getStepConfig();
    
    if (step === 0) return true;
    
    if (!hasStoredWeight && step === config.currentWeightStep) {
      return preferences.currentWeightKg !== null && preferences.currentWeightKg > 0;
    }
    
    if (step === config.targetWeightStep) {
      return preferences.targetWeightKg !== null && preferences.targetWeightKg > 0;
    }
    
    if (step === config.timelineStep) {
      return preferences.targetTimelineWeeks >= 1;
    }
    
    if (step === config.foodStep) return true;
    if (step === config.allergiesStep) return true;
    if (step === config.activityStep) return true;
    
    return true;
  };

  const startGeneration = () => {
    setWizardStep(1);
  };

  const updateStepStatus = (stepId: string, status: 'pending' | 'loading' | 'completed') => {
    setGenerationSteps(prev => 
      prev.map(step => 
        step.id === stepId ? { ...step, status } : step
      )
    );
  };

  const generateProtocol = async () => {
    // Re-check protocol generation allowance before generating
    const checkResult = await checkProtocolGeneration();
    if (!checkResult.allowed) {
      setError(checkResult.message);
      return;
    }

    setIsGenerating(true);
    setError('');
    setProgress(0);
    setGenerationSteps(initialGenerationSteps);

    // Save current weight if it was entered during the wizard
    if (!hasStoredWeight && preferences.currentWeightKg) {
      await saveCurrentWeight(preferences.currentWeightKg);
      setCurrentWeight(preferences.currentWeightKg);
    }

    const stepIds = initialGenerationSteps.map(s => s.id);
    const stepDuration = 1200;

    for (let i = 0; i < stepIds.length; i++) {
      const currentStepId = stepIds[i];
      updateStepStatus(currentStepId, 'loading');
      setProgress(((i + 0.5) / stepIds.length) * 85);
      await new Promise(resolve => setTimeout(resolve, stepDuration));
      updateStepStatus(currentStepId, 'completed');
      setProgress(((i + 1) / stepIds.length) * 85);
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-protocol`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ preferences })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate protocol');
      }

      const data = await response.json();
      
      // Record the protocol generation AFTER successful generation
      await recordProtocolGeneration();
      
      setProgress(100);
      setSuccess(true);

      setTimeout(() => {
        onComplete(data.protocol);
      }, 1500);
    } catch (err) {
      console.error('Error generating protocol:', err);
      setError('Failed to generate your protocol. Please try again.');
      setIsGenerating(false);
    }
  };

  const config = getStepConfig();
  const totalSteps = config.totalSteps;

  const getTimelineColor = (weeks: number, isSelected: boolean) => {
    const option = timelineOptions.find(o => o.weeks === weeks);
    if (!isSelected) return 'border-gray-200 hover:border-gray-300';
    
    switch (option?.color) {
      case 'orange': return 'border-orange-500 bg-orange-50';
      case 'green': return 'border-green-500 bg-green-50';
      case 'blue': return 'border-blue-500 bg-blue-50';
      case 'purple': return 'border-purple-500 bg-purple-50';
      default: return 'border-orange-500 bg-orange-50';
    }
  };

  // Render current weight step (only if weight not stored)
  const renderCurrentWeightStep = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-4 sm:mb-6">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <Scale size={28} className="text-green-600 sm:w-8 sm:h-8" />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          What's Your Current Weight?
        </h3>
        <p className="text-gray-600 text-sm">
          This helps us create an accurate plan for you
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Current Weight (kg)
        </label>
        <input
          type="number"
          value={preferences.currentWeightKg || ''}
          onChange={(e) => {
            const weight = parseFloat(e.target.value) || null;
            setPreferences({ 
              ...preferences, 
              currentWeightKg: weight,
              targetWeightKg: weight // Set target weight same as current initially
            });
          }}
          placeholder="Enter your current weight"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none text-lg font-semibold"
        />
        <p className="text-xs text-gray-500 mt-2">
          We'll save this to your profile
        </p>
      </div>

      {preferences.currentWeightKg && preferences.currentWeightKg > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Check size={18} className="text-green-600" />
            <p className="text-sm font-semibold text-green-800">
              Current weight: {preferences.currentWeightKg} kg
            </p>
          </div>
        </div>
      )}
    </div>
  );

  // Render target weight step
  const renderTargetWeightStep = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-4 sm:mb-6">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <Target size={28} className="text-orange-600 sm:w-8 sm:h-8" />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          What's Your Weight Goal?
        </h3>
        <p className="text-gray-600 text-sm">
          {(currentWeight || preferences.currentWeightKg) && 
            `Your current weight: ${currentWeight || preferences.currentWeightKg} kg`
          }
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Target Weight (kg)
        </label>
        <input
          type="number"
          value={preferences.targetWeightKg || ''}
          onChange={(e) => setPreferences({ ...preferences, targetWeightKg: parseFloat(e.target.value) || null })}
          placeholder="Enter target weight"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none text-lg font-semibold"
        />
        <p className="text-xs text-gray-500 mt-2">
          Leave as current weight if you want to maintain
        </p>
      </div>

      {preferences.targetWeightKg && (currentWeight || preferences.currentWeightKg) && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-blue-900">
            {(() => {
              const current = currentWeight || preferences.currentWeightKg || 0;
              if (preferences.targetWeightKg < current) {
                return `Goal: Lose ${(current - preferences.targetWeightKg).toFixed(1)} kg`;
              } else if (preferences.targetWeightKg > current) {
                return `Goal: Gain ${(preferences.targetWeightKg - current).toFixed(1)} kg`;
              } else {
                return 'Goal: Maintain current weight';
              }
            })()}
          </p>
        </div>
      )}
    </div>
  );

  // Render timeline step
  const renderTimelineStep = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-4 sm:mb-6">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <Calendar size={28} className="text-blue-600 sm:w-8 sm:h-8" />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Timeline for Your Goal
        </h3>
        <p className="text-gray-600 text-sm">
          Choose your program duration
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {timelineOptions.map(option => (
          <button
            key={option.weeks}
            onClick={() => setPreferences({ ...preferences, targetTimelineWeeks: option.weeks })}
            className={`p-4 rounded-xl border-2 transition-all relative ${getTimelineColor(option.weeks, preferences.targetTimelineWeeks === option.weeks)}`}
          >
            {option.weeks === 4 && (
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                Popular
              </span>
            )}
            <p className="font-bold text-gray-900 text-lg">{option.label}</p>
            <p className="text-xs text-gray-600 mt-1">{option.desc}</p>
          </button>
        ))}
      </div>

      <div className="bg-gray-50 rounded-xl p-4 mt-4">
        <p className="text-sm text-gray-700">
          {preferences.targetTimelineWeeks === 2 && (
            <>
              <span className="font-semibold">2-Week Quick Start:</span> Ideal for jumpstarting your health journey with focused, intensive routines.
            </>
          )}
          {preferences.targetTimelineWeeks === 4 && (
            <>
              <span className="font-semibold">4-Week Program:</span> Our most popular option. Enough time to build lasting habits while seeing real results.
            </>
          )}
          {preferences.targetTimelineWeeks === 8 && (
            <>
              <span className="font-semibold">8-Week Journey:</span> A sustainable approach allowing gradual progression and deeper habit formation.
            </>
          )}
          {preferences.targetTimelineWeeks === 12 && (
            <>
              <span className="font-semibold">12-Week Transformation:</span> Complete lifestyle overhaul with comprehensive phases for maximum results.
            </>
          )}
        </p>
      </div>
    </div>
  );

  // Render food preferences step
  const renderFoodStep = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-4 sm:mb-6">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <Apple size={28} className="text-green-600 sm:w-8 sm:h-8" />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Food Preferences
        </h3>
        <p className="text-gray-600 text-sm">
          Select foods you enjoy eating
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Dietary Type
        </label>
        <select
          value={preferences.dietaryRestrictions}
          onChange={(e) => setPreferences({ ...preferences, dietaryRestrictions: e.target.value })}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none"
        >
          <option value="">No restrictions</option>
          <option value="vegetarian">Vegetarian</option>
          <option value="vegan">Vegan</option>
          <option value="pescatarian">Pescatarian</option>
          <option value="keto">Keto</option>
          <option value="paleo">Paleo</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Foods You Like (Optional)
        </label>
        <div className="flex flex-wrap gap-2">
          {commonFoods.map(food => (
            <button
              key={food}
              onClick={() => setPreferences({
                ...preferences,
                foodPreferences: toggleArrayItem(preferences.foodPreferences, food)
              })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                preferences.foodPreferences.includes(food)
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {food}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // Render allergies step
  const renderAllergiesStep = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-4 sm:mb-6">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <AlertCircle size={28} className="text-red-600 sm:w-8 sm:h-8" />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Allergies & Restrictions
        </h3>
        <p className="text-gray-600 text-sm">
          Help us keep you safe
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Select Any Allergies
        </label>
        <div className="flex flex-wrap gap-2">
          {commonAllergies.map(allergy => (
            <button
              key={allergy}
              onClick={() => setPreferences({
                ...preferences,
                allergies: toggleArrayItem(preferences.allergies, allergy)
              })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                preferences.allergies.includes(allergy)
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {allergy}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // Render activity level step
  const renderActivityStep = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-4 sm:mb-6">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <Activity size={28} className="text-teal-600 sm:w-8 sm:h-8" />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Activity Level
        </h3>
        <p className="text-gray-600 text-sm">
          How active are you currently?
        </p>
      </div>

      <div className="space-y-3">
        {[
          { value: 'sedentary', label: 'Sedentary', desc: 'Little to no exercise' },
          { value: 'lightly_active', label: 'Lightly Active', desc: 'Exercise 1-3 days/week' },
          { value: 'moderately_active', label: 'Moderately Active', desc: 'Exercise 3-5 days/week' },
          { value: 'very_active', label: 'Very Active', desc: 'Exercise 6-7 days/week' },
          { value: 'extremely_active', label: 'Extremely Active', desc: 'Intense exercise daily' }
        ].map(option => (
          <button
            key={option.value}
            onClick={() => setPreferences({ ...preferences, activityLevel: option.value })}
            className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
              preferences.activityLevel === option.value
                ? 'border-orange-500 bg-orange-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <p className="font-bold text-gray-900">{option.label}</p>
            <p className="text-sm text-gray-600 mt-1">{option.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );

  // Render the correct step based on wizardStep and config
  const renderWizardStep = () => {
    const config = getStepConfig();

    if (!hasStoredWeight) {
      // Flow with current weight step
      if (wizardStep === config.currentWeightStep) return renderCurrentWeightStep();
      if (wizardStep === config.targetWeightStep) return renderTargetWeightStep();
      if (wizardStep === config.timelineStep) return renderTimelineStep();
      if (wizardStep === config.foodStep) return renderFoodStep();
      if (wizardStep === config.allergiesStep) return renderAllergiesStep();
      if (wizardStep === config.activityStep) return renderActivityStep();
    } else {
      // Flow without current weight step
      if (wizardStep === config.targetWeightStep) return renderTargetWeightStep();
      if (wizardStep === config.timelineStep) return renderTimelineStep();
      if (wizardStep === config.foodStep) return renderFoodStep();
      if (wizardStep === config.allergiesStep) return renderAllergiesStep();
      if (wizardStep === config.activityStep) return renderActivityStep();
    }

    return null;
  };

  // Loading state while checking profile or protocol eligibility
  if (isLoadingProfile || isCheckingProtocol) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
        <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl overflow-hidden p-8">
          <div className="flex flex-col items-center justify-center">
            <Loader2 size={40} className="text-orange-500 animate-spin mb-4" />
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // User cannot generate protocol - show upgrade/wait message
  if (protocolCheckResult && !protocolCheckResult.allowed) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
        <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center z-10"
          >
            <X size={18} className="text-gray-600" />
          </button>

          <div className="p-6 sm:p-8">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <LongevityBot size={120} mode="thinking" />
                <div className="absolute -top-1 -right-1 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  {isPro ? <Zap size={20} className="text-orange-500" /> : <Lock size={20} className="text-orange-500" />}
                </div>
              </div>
            </div>

            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {isPro ? 'Cooldown Active' : 'Protocol Limit Reached'}
              </h2>
              <p className="text-gray-600 text-sm">{protocolCheckResult.message}</p>
            </div>

            {/* Pro user on cooldown */}
            {isPro && protocolCheckResult.daysUntilNext && (
              <div className="bg-orange-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Next available in</span>
                  <span className="text-2xl font-bold text-orange-600">{protocolCheckResult.daysUntilNext} days</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">Pro members can regenerate every 5 days</p>
              </div>
            )}

            {/* Free user who already generated */}
            {!isPro && (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Crown className="w-6 h-6 text-amber-500" />
                    <h3 className="font-bold text-gray-900">Upgrade to Pro</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2"><Check size={14} className="text-green-500" />Regenerate protocol every 5 days</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-green-500" />8 AI food scans per day</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-green-500" />Advanced analytics & insights</li>
                  </ul>
                </div>

                {onUpgrade && (
                  <button
                    onClick={onUpgrade}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2"
                  >
                    <Sparkles size={18} />
                    Upgrade to Pro
                  </button>
                )}
              </div>
            )}

            <button onClick={onClose} className="w-full mt-4 py-3 text-gray-600 font-semibold hover:text-gray-900">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expired trial
  if (isExpired) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
        <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden relative">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center z-10">
            <X size={18} className="text-gray-600" />
          </button>

          <div className="p-6 sm:p-8">
            <div className="flex justify-center mb-6">
              <LongevityBot size={120} mode="thinking" />
            </div>

            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-bold mb-3">
                <AlertCircle size={12} />TRIAL EXPIRED
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Your Trial Has Ended</h2>
              <p className="text-gray-600 text-sm">Upgrade to Pro to continue generating protocols</p>
            </div>

            {onUpgrade && (
              <button onClick={onUpgrade} className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2">
                <Sparkles size={18} />Upgrade to Pro
              </button>
            )}
            <button onClick={onClose} className="w-full mt-4 py-3 text-gray-600 font-semibold hover:text-gray-900">Maybe Later</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
      <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {wizardStep === 0 ? (
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 sm:p-8">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10"
              >
                <X size={18} className="text-gray-600" />
              </button>

              <div className="flex justify-center mb-4 sm:mb-6">
                <LongevityBot size={120} mode="success" />
              </div>

              <div className="text-center mb-4 sm:mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-xs font-bold uppercase tracking-wide mb-3">
                  <Sparkles size={14} />
                  AI-Powered
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  Generate Your Personalized Protocol
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Answer a few questions to create your customized health plan
                </p>
              </div>

              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={14} className="text-white" strokeWidth={3} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Reduce Biological Age</p>
                    <p className="text-gray-600 text-xs">Optimize your health markers</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={14} className="text-white" strokeWidth={3} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Boost Energy & Focus</p>
                    <p className="text-gray-600 text-xs">Feel more energized daily</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-teal-50 rounded-xl">
                  <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={14} className="text-white" strokeWidth={3} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Achieve Mental Peace</p>
                    <p className="text-gray-600 text-xs">Reduce stress naturally</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-xl">
                  <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={14} className="text-white" strokeWidth={3} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Reach Healthy Weight</p>
                    <p className="text-gray-600 text-xs">Sustainable weight management</p>
                  </div>
                </div>
              </div>

              <button
                onClick={startGeneration}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-3 sm:py-4 rounded-xl font-bold text-base transition-all shadow-lg"
              >
                Get Started
              </button>

              <p className="text-center text-xs text-gray-500 mt-3 mb-2">
                Takes about 2 minutes to complete
              </p>
            </div>
          </div>
        ) : success ? (
          <div className="p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <Check size={40} className="text-green-600" strokeWidth={3} />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Protocol Generated!
            </h2>
            <p className="text-gray-600 text-sm">
              Your personalized {preferences.targetTimelineWeeks}-week plan is ready
            </p>
          </div>
        ) : isGenerating ? (
          <div className="p-6 sm:p-8">
            <div className="flex justify-center mb-6">
              <LongevityBot size={100} mode="listening" />
            </div>

            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Creating Your {preferences.targetTimelineWeeks}-Week Protocol
              </h2>
              <p className="text-gray-500 text-sm">
                Our AI is crafting your personalized health journey
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {generationSteps.map((step) => {
                const IconComponent = step.icon;
                return (
                  <div
                    key={step.id}
                    className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-500 ${
                      step.status === 'completed'
                        ? 'bg-green-50 border border-green-200'
                        : step.status === 'loading'
                        ? 'bg-orange-50 border border-orange-200'
                        : 'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                        step.status === 'completed'
                          ? 'bg-green-500'
                          : step.status === 'loading'
                          ? 'bg-orange-500'
                          : 'bg-gray-200'
                      }`}
                    >
                      {step.status === 'completed' ? (
                        <Check size={20} className="text-white" strokeWidth={3} />
                      ) : step.status === 'loading' ? (
                        <Loader2 size={20} className="text-white animate-spin" />
                      ) : (
                        <IconComponent size={20} className="text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`font-semibold text-sm transition-colors ${
                          step.status === 'completed'
                            ? 'text-green-700'
                            : step.status === 'loading'
                            ? 'text-orange-700'
                            : 'text-gray-400'
                        }`}
                      >
                        {step.label}
                      </p>
                      <p
                        className={`text-xs truncate transition-colors ${
                          step.status === 'loading'
                            ? 'text-orange-600'
                            : step.status === 'completed'
                            ? 'text-green-600'
                            : 'text-gray-400'
                        }`}
                      >
                        {step.status === 'loading' ? step.description : step.status === 'completed' ? 'Complete' : 'Waiting...'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600">Overall Progress</span>
                <span className="font-bold text-orange-600">{Math.round(progress)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-500 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <p className="text-red-700 text-sm font-medium">{error}</p>
                <button
                  onClick={generateProtocol}
                  className="mt-3 text-red-600 text-sm font-bold underline"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <button
                  onClick={() => wizardStep > 1 ? setWizardStep(wizardStep - 1) : setWizardStep(0)}
                  className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <ChevronLeft size={20} className="text-gray-600" />
                </button>
                <div className="text-center flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    Step {wizardStep} of {totalSteps}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X size={18} className="text-gray-600" />
                </button>
              </div>

              <div className="flex gap-1.5">
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 flex-1 rounded-full transition-all ${
                      i + 1 <= wizardStep ? 'bg-orange-500' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 sm:p-8">
              {renderWizardStep()}
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 bg-white">
              <button
                onClick={() => {
                  if (wizardStep < totalSteps) {
                    if (canProceedFromStep(wizardStep)) {
                      setWizardStep(wizardStep + 1);
                    }
                  } else {
                    generateProtocol();
                  }
                }}
                disabled={!canProceedFromStep(wizardStep)}
                className={`w-full py-3 sm:py-4 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-2 ${
                  canProceedFromStep(wizardStep)
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {wizardStep < totalSteps ? 'Continue' : `Generate My ${preferences.targetTimelineWeeks}-Week Protocol`}
                {wizardStep < totalSteps && <ChevronRight size={20} />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}